import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Navigation from '../components/Navigation';
import { toast } from 'react-toastify';
import { Zap, Smartphone } from 'lucide-react';

function Recharge() {
  const [tab, setTab] = useState('mobile');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);

  const [mobile, setMobile] = useState('');
  const [operatorCode, setOperatorCode] = useState('JO');
  const [amount, setAmount] = useState('');
  const [circle, setCircle] = useState('10');

  const navigate = useNavigate();

  const operators = [
    { code: 'JO', name: 'Jio' },
    { code: 'AT', name: 'Airtel' },
    { code: 'VI', name: 'VI (Vodafone Idea)' },
    { code: 'BS', name: 'BSNL' },
  ];

  const circles = [
    { code: '10', name: 'Delhi' },
    { code: '97', name: 'UP West' },
    { code: '54', name: 'UP East' },
    { code: '90', name: 'Maharashtra' },
    { code: '92', name: 'Mumbai' },
    { code: '98', name: 'Gujarat' },
    { code: '70', name: 'Rajasthan' },
    { code: '06', name: 'Karnataka' },
    { code: '40', name: 'Chennai' },
    { code: '94', name: 'Tamil Nadu' },
    { code: '50', name: 'All India' },
  ];

  useEffect(() => {
    fetchStatus();
    fetchHistory();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/Recharge/status');
      setStatus(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await api.get('/Recharge/history');
      setHistory(res.data.data || []);
    } catch (err) {
      console.log(err);
    }
  };

  const handleMobileRecharge = async (e) => {
    e.preventDefault();

    if (!mobile || mobile.length !== 10) {
      toast.error('Valid 10-digit mobile number enter करें');
      return;
    }
    if (!amount || Number(amount) < 10) {
      toast.error('Minimum amount ₹10');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/Recharge/mobile', {
        mobile,
        operatorCode,
        amount: Number(amount),
        circle,
      });
      toast.success(res.data.message || 'Recharge successful!');
      setMobile('');
      setAmount('');
      fetchStatus();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Recharge failed');
    } finally {
      setLoading(false);
    }
  };

  const handleFreeRecharge = async () => {
    setLoading(true);
    try {
      const res = await api.post('/Recharge/do');
      toast.success(res.data.message);
      fetchStatus();
      fetchHistory();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 pb-24">
      {/* Header */}
      <div className="bg-green-600 text-white p-4">
        <button onClick={() => navigate(-1)} className="text-sm mb-2">
          ← Back
        </button>
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Zap size={22} /> Recharge
        </h2>
      </div>

      {/* Tabs */}
      <div className="mx-4 mt-4 flex bg-white rounded-xl shadow overflow-hidden">
        <button
          onClick={() => setTab('mobile')}
          className={`flex-1 py-3 text-sm font-semibold ${
            tab === 'mobile' ? 'bg-green-600 text-white' : 'text-gray-600'
          }`}
        >
          <Smartphone size={16} className="inline mr-1" />
          Mobile Recharge
        </button>
        <button
          onClick={() => setTab('free')}
          className={`flex-1 py-3 text-sm font-semibold ${
            tab === 'free' ? 'bg-green-600 text-white' : 'text-gray-600'
          }`}
        >
          Free Plan
        </button>
      </div>

      <div className="p-4">
        {/* ===== MOBILE RECHARGE TAB ===== */}
        {tab === 'mobile' && (
          <form
            onSubmit={handleMobileRecharge}
            className="bg-white rounded-2xl p-5 shadow space-y-4"
          >
            <div>
              <label className="text-sm text-gray-600">Mobile Number</label>
              <input
                type="tel"
                maxLength={10}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                placeholder="9876543210"
                className="w-full mt-1 border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div>
              <label className="text-sm text-gray-600">Operator</label>
              <select
                value={operatorCode}
                onChange={(e) => setOperatorCode(e.target.value)}
                className="w-full mt-1 border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {operators.map((op) => (
                  <option key={op.code} value={op.code}>
                    {op.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Circle</label>
              <select
                value={circle}
                onChange={(e) => setCircle(e.target.value)}
                className="w-full mt-1 border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {circles.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm text-gray-600">Amount (₹)</label>
              <input
                type="number"
                min={10}
                max={25000}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="10"
                className="w-full mt-1 border rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-green-600 text-white font-bold py-3 rounded-xl disabled:opacity-50"
            >
              {loading ? 'Processing...' : `Recharge ₹${amount || '0'}`}
            </button>
          </form>
        )}

        {/* ===== FREE PLAN TAB ===== */}
        {tab === 'free' && status && (
          <div className="bg-white rounded-2xl p-5 shadow space-y-4">
            <div
              className={`p-3 rounded-xl text-sm ${
                status.isCurrentlyFree
                  ? 'bg-green-50 text-green-700'
                  : status.isFreeEligible
                  ? 'bg-blue-50 text-blue-700'
                  : 'bg-yellow-50 text-yellow-700'
              }`}
            >
              {status.message}
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-gray-50 p-3 rounded-xl">
                <p className="text-gray-500">Direct Members</p>
                <p className="font-bold text-lg">{status.directMembers}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-xl">
                <p className="text-gray-500">Required</p>
                <p className="font-bold text-lg">{status.requiredMembers}</p>
              </div>
            </div>

            {status.isCurrentlyFree && status.freeValidTill && (
              <p className="text-center text-green-600 font-semibold">
                Free till: {new Date(status.freeValidTill).toLocaleDateString()}
              </p>
            )}

            {!status.isCurrentlyFree && (
              <button
                onClick={handleFreeRecharge}
                disabled={loading}
                className="w-full bg-green-600 text-white font-bold py-3 rounded-xl disabled:opacity-50"
              >
                {loading
                  ? 'Processing...'
                  : status.isFreeEligible
                  ? `Activate ${status.freeMonths} Months Free`
                  : `Recharge ₹${status.rechargeAmount}`}
              </button>
            )}
          </div>
        )}

        {/* ===== RECHARGE HISTORY ===== */}
        <div className="mt-6">
          <h3 className="font-semibold text-gray-700 mb-3">Recent Recharges</h3>

          {history.length === 0 ? (
            <div className="bg-white rounded-2xl p-6 text-center text-gray-400 shadow">
              No recharges yet
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="bg-white rounded-2xl p-4 shadow flex justify-between items-center"
                >
                  <div>
                    <p className="font-bold text-gray-800">
                      {h.mobile || 'Membership'}{' '}
                      {h.operatorCode ? `(${h.operatorCode})` : ''}
                    </p>
                    <p className="text-xs text-gray-500">
                      {new Date(h.rechargeDate).toLocaleString()}
                    </p>
                    {h.memberTxnId && (
                      <p className="text-xs text-gray-400 font-mono">
                        {h.memberTxnId}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold">₹{h.amount}</p>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        h.status === 'Success'
                          ? 'bg-green-100 text-green-700'
                          : h.status === 'Processing'
                          ? 'bg-yellow-100 text-yellow-700'
                          : h.isFree && !h.mobile
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {h.isFree && !h.mobile ? 'Free' : h.status || 'Done'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <Navigation />
    </div>
  );
}

export default Recharge;